import * as z from 'zod';

export const CreateBackInStockNotificationRequestRequest = z.object({
  request: z
    .object({
      _id: z
        .string()
        .describe('Notification request ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      catalogReference: z
        .object({
          catalogItemId: z
            .string()
            .describe('ID of the item within the catalog it belongs to.')
            .min(1)
            .max(36),
          appId: z
            .string()
            .describe(
              'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
            )
            .min(1),
          options: z
            .record(z.string(), z.any())
            .describe(
              'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Catalog and item reference that the notification request is for.\n\nIncludes IDs for the catalog and item it came from, as well as additional, optional information.\n\nBack in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).\nThe `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.'
        ),
      email: z
        .string()
        .describe(
          'Email address to send notification to about item being back in stock.'
        )
        .email(),
      contactId: z
        .string()
        .describe(
          'Contact ID for the contact with this `email`.\n\nIf a contact does not already exist with the email address submitted when creating this notification request, then a new contact is created.\nFor more information about contacts, see the Contacts API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNSPECIFIED',
          'RECEIVED',
          'PROCESSING',
          'NOTIFICATION_SENT',
          'FAILED',
        ])
        .optional(),
      autoNotified: z
        .boolean()
        .describe(
          'Whether a notification was sent automatically.\n\n`autoNotified` is empty when the notification request is created and is not returned until\nthe field has a value. `autoNotified` receives a value when a notification email is sent for this request object.\n\n`autoNotified` sets to `TRUE` if the notification is sent through the site, either automatically or with the Report Items Back In Stock method.\nIf the notification email is sent offline but the `status` is updated with the Mark As Notification Sent method,\nthen `autoNotified` sets to `FALSE`.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the notification request was created.')
        .optional()
        .nullable(),
      itemUrl: z
        .string()
        .describe('Item URL for this notification request.')
        .url()
        .optional()
        .nullable(),
    })
    .describe(
      "Notification request information.\n\nIncludes details for the out of stock item and the email address\nrequesting to be notified when it's back in stock."
    ),
  itemDetails: z
    .object({
      name: z.string().describe('Item name.'),
      price: z.string().describe('Item price.'),
      image: z.string().describe('Item image.').optional(),
    })
    .describe(
      'Item details to include in the notification when the item is back in stock.'
    ),
});
export const CreateBackInStockNotificationRequestResponse = z.object({
  _id: z
    .string()
    .describe('Notification request ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  catalogReference: z
    .object({
      catalogItemId: z
        .string()
        .describe('ID of the item within the catalog it belongs to.')
        .min(1)
        .max(36)
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
        )
        .min(1)
        .optional(),
      options: z
        .record(z.string(), z.any())
        .describe(
          'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Catalog and item reference that the notification request is for.\n\nIncludes IDs for the catalog and item it came from, as well as additional, optional information.\n\nBack in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).\nThe `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.'
    )
    .optional(),
  email: z
    .string()
    .describe(
      'Email address to send notification to about item being back in stock.'
    )
    .email()
    .optional(),
  contactId: z
    .string()
    .describe(
      'Contact ID for the contact with this `email`.\n\nIf a contact does not already exist with the email address submitted when creating this notification request, then a new contact is created.\nFor more information about contacts, see the Contacts API.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  status: z
    .enum([
      'UNSPECIFIED',
      'RECEIVED',
      'PROCESSING',
      'NOTIFICATION_SENT',
      'FAILED',
    ])
    .describe(
      'Status of the notification.\n\n`status` is set to `RECEIVED` when the notification request is created.\nThe `status` changes once a notification email is sent for this request object:\n+ When a notification email is sent through the site, either automatically or with the Report Items Back In Stock method, then the `status` is briefly set to `PROCESSING` and then set to `NOTIFICATION_SENT` if the email is successful, and `FAILED` if it fails.\n+ When a notification email is sent offline, use the Mark As Notification Sent method to set `status` to `NOTIFICATION_SENT`.'
    )
    .optional(),
  autoNotified: z
    .boolean()
    .describe(
      'Whether a notification was sent automatically.\n\n`autoNotified` is empty when the notification request is created and is not returned until\nthe field has a value. `autoNotified` receives a value when a notification email is sent for this request object.\n\n`autoNotified` sets to `TRUE` if the notification is sent through the site, either automatically or with the Report Items Back In Stock method.\nIf the notification email is sent offline but the `status` is updated with the Mark As Notification Sent method,\nthen `autoNotified` sets to `FALSE`.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the notification request was created.')
    .optional()
    .nullable(),
  itemUrl: z
    .string()
    .describe('Item URL for this notification request.')
    .url()
    .optional()
    .nullable(),
});
export const GetBackInStockNotificationRequestRequest = z.object({
  _id: z
    .string()
    .describe('ID of the notification request to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetBackInStockNotificationRequestResponse = z.object({
  _id: z
    .string()
    .describe('Notification request ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  catalogReference: z
    .object({
      catalogItemId: z
        .string()
        .describe('ID of the item within the catalog it belongs to.')
        .min(1)
        .max(36)
        .optional(),
      appId: z
        .string()
        .describe(
          'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
        )
        .min(1)
        .optional(),
      options: z
        .record(z.string(), z.any())
        .describe(
          'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Catalog and item reference that the notification request is for.\n\nIncludes IDs for the catalog and item it came from, as well as additional, optional information.\n\nBack in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).\nThe `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.'
    )
    .optional(),
  email: z
    .string()
    .describe(
      'Email address to send notification to about item being back in stock.'
    )
    .email()
    .optional(),
  contactId: z
    .string()
    .describe(
      'Contact ID for the contact with this `email`.\n\nIf a contact does not already exist with the email address submitted when creating this notification request, then a new contact is created.\nFor more information about contacts, see the Contacts API.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  status: z
    .enum([
      'UNSPECIFIED',
      'RECEIVED',
      'PROCESSING',
      'NOTIFICATION_SENT',
      'FAILED',
    ])
    .describe(
      'Status of the notification.\n\n`status` is set to `RECEIVED` when the notification request is created.\nThe `status` changes once a notification email is sent for this request object:\n+ When a notification email is sent through the site, either automatically or with the Report Items Back In Stock method, then the `status` is briefly set to `PROCESSING` and then set to `NOTIFICATION_SENT` if the email is successful, and `FAILED` if it fails.\n+ When a notification email is sent offline, use the Mark As Notification Sent method to set `status` to `NOTIFICATION_SENT`.'
    )
    .optional(),
  autoNotified: z
    .boolean()
    .describe(
      'Whether a notification was sent automatically.\n\n`autoNotified` is empty when the notification request is created and is not returned until\nthe field has a value. `autoNotified` receives a value when a notification email is sent for this request object.\n\n`autoNotified` sets to `TRUE` if the notification is sent through the site, either automatically or with the Report Items Back In Stock method.\nIf the notification email is sent offline but the `status` is updated with the Mark As Notification Sent method,\nthen `autoNotified` sets to `FALSE`.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the notification request was created.')
    .optional()
    .nullable(),
  itemUrl: z
    .string()
    .describe('Item URL for this notification request.')
    .url()
    .optional()
    .nullable(),
});
export const DeleteBackInStockNotificationRequestRequest = z.object({
  _id: z
    .string()
    .describe('ID of the notification request to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteBackInStockNotificationRequestResponse = z.object({});
export const MarkAsNotificationSentRequest = z.object({
  _id: z
    .string()
    .describe('ID of the notification request to mark.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const MarkAsNotificationSentResponse = z.object({
  request: z
    .object({
      _id: z
        .string()
        .describe('Notification request ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      catalogReference: z
        .object({
          catalogItemId: z
            .string()
            .describe('ID of the item within the catalog it belongs to.')
            .min(1)
            .max(36)
            .optional(),
          appId: z
            .string()
            .describe(
              'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
            )
            .min(1)
            .optional(),
          options: z
            .record(z.string(), z.any())
            .describe(
              'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Catalog and item reference that the notification request is for.\n\nIncludes IDs for the catalog and item it came from, as well as additional, optional information.\n\nBack in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).\nThe `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.'
        )
        .optional(),
      email: z
        .string()
        .describe(
          'Email address to send notification to about item being back in stock.'
        )
        .email()
        .optional(),
      contactId: z
        .string()
        .describe(
          'Contact ID for the contact with this `email`.\n\nIf a contact does not already exist with the email address submitted when creating this notification request, then a new contact is created.\nFor more information about contacts, see the Contacts API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNSPECIFIED',
          'RECEIVED',
          'PROCESSING',
          'NOTIFICATION_SENT',
          'FAILED',
        ])
        .describe(
          'Status of the notification.\n\n`status` is set to `RECEIVED` when the notification request is created.\nThe `status` changes once a notification email is sent for this request object:\n+ When a notification email is sent through the site, either automatically or with the Report Items Back In Stock method, then the `status` is briefly set to `PROCESSING` and then set to `NOTIFICATION_SENT` if the email is successful, and `FAILED` if it fails.\n+ When a notification email is sent offline, use the Mark As Notification Sent method to set `status` to `NOTIFICATION_SENT`.'
        )
        .optional(),
      autoNotified: z
        .boolean()
        .describe(
          'Whether a notification was sent automatically.\n\n`autoNotified` is empty when the notification request is created and is not returned until\nthe field has a value. `autoNotified` receives a value when a notification email is sent for this request object.\n\n`autoNotified` sets to `TRUE` if the notification is sent through the site, either automatically or with the Report Items Back In Stock method.\nIf the notification email is sent offline but the `status` is updated with the Mark As Notification Sent method,\nthen `autoNotified` sets to `FALSE`.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the notification request was created.')
        .optional()
        .nullable(),
      itemUrl: z
        .string()
        .describe('Item URL for this notification request.')
        .url()
        .optional()
        .nullable(),
    })
    .describe('Marked back in stock notification request.')
    .optional(),
});
export const QueryBackInStockNotificationRequestsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          contactId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          autoNotified: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          itemUrl: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'contactId',
                'status',
                'autoNotified',
                '_createdDate',
                'itemUrl',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryBackInStockNotificationRequestsResponse = z.object({
  requests: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Notification request ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Catalog and item reference that the notification request is for.\n\nIncludes IDs for the catalog and item it came from, as well as additional, optional information.\n\nBack in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).\nThe `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.'
          )
          .optional(),
        email: z
          .string()
          .describe(
            'Email address to send notification to about item being back in stock.'
          )
          .email()
          .optional(),
        contactId: z
          .string()
          .describe(
            'Contact ID for the contact with this `email`.\n\nIf a contact does not already exist with the email address submitted when creating this notification request, then a new contact is created.\nFor more information about contacts, see the Contacts API.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNSPECIFIED',
            'RECEIVED',
            'PROCESSING',
            'NOTIFICATION_SENT',
            'FAILED',
          ])
          .describe(
            'Status of the notification.\n\n`status` is set to `RECEIVED` when the notification request is created.\nThe `status` changes once a notification email is sent for this request object:\n+ When a notification email is sent through the site, either automatically or with the Report Items Back In Stock method, then the `status` is briefly set to `PROCESSING` and then set to `NOTIFICATION_SENT` if the email is successful, and `FAILED` if it fails.\n+ When a notification email is sent offline, use the Mark As Notification Sent method to set `status` to `NOTIFICATION_SENT`.'
          )
          .optional(),
        autoNotified: z
          .boolean()
          .describe(
            'Whether a notification was sent automatically.\n\n`autoNotified` is empty when the notification request is created and is not returned until\nthe field has a value. `autoNotified` receives a value when a notification email is sent for this request object.\n\n`autoNotified` sets to `TRUE` if the notification is sent through the site, either automatically or with the Report Items Back In Stock method.\nIf the notification email is sent offline but the `status` is updated with the Mark As Notification Sent method,\nthen `autoNotified` sets to `FALSE`.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the notification request was created.')
          .optional()
          .nullable(),
        itemUrl: z
          .string()
          .describe('Item URL for this notification request.')
          .url()
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('The number of items returned in this response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe(
          'The offset which was requested. Returned if offset paging was used.'
        )
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'The total number of items that match the query. Returned if offset paging was used.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through result pages. Returned if cursor paging was used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const GetBackInStockNotificationRequestsCountByCatalogReferencesRequest =
  z.object({
    catalogReferences: z
      .array(
        z.object({
          catalogItemId: z
            .string()
            .describe('ID of the item within the catalog it belongs to.')
            .min(1)
            .max(36)
            .optional(),
          appId: z
            .string()
            .describe(
              'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
            )
            .min(1)
            .optional(),
          options: z
            .record(z.string(), z.any())
            .describe(
              'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
            )
            .optional()
            .nullable(),
        })
      )
      .min(1)
      .max(100),
  });
export const GetBackInStockNotificationRequestsCountByCatalogReferencesResponse =
  z.object({
    countsPerCatalogReference: z
      .array(
        z.object({
          catalogReference: z
            .object({
              catalogItemId: z
                .string()
                .describe('ID of the item within the catalog it belongs to.')
                .min(1)
                .max(36)
                .optional(),
              appId: z
                .string()
                .describe(
                  'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                )
                .min(1)
                .optional(),
              options: z
                .record(z.string(), z.any())
                .describe(
                  'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Catalog and item reference.\n\nIncludes IDs and additional, optional information related to the item.'
            )
            .optional(),
          count: z
            .number()
            .int()
            .describe(
              'The number of unique back in stock requests for given `catalogReference`.'
            )
            .optional(),
        })
      )
      .optional(),
  });
export const ReportItemsBackInStockRequest = z.object({
  itemDetails: z
    .object({
      name: z.string().describe('Item name.'),
      price: z.string().describe('Item price.'),
      image: z.string().describe('Item image.').optional(),
    })
    .describe(
      'Item details to use in notifications.\n\n`itemDetails` may populate dynamic values in the notification template, as follows:\n+ `itemDetails.name` passes to the template as `item.name`\n+ `itemDetails.price` passes to the template as `item.price`\n+ `itemDetails.image.url` passes to the template as `item.image.url`\n\nUse `extraAutomationTemplateParameters` to pass additional dynamic values.'
    ),
  options: z
    .object({
      catalogReference: z
        .object({
          catalogItemId: z
            .string()
            .describe('ID of the item within the catalog it belongs to.')
            .min(1)
            .max(36)
            .optional(),
          appId: z
            .string()
            .describe(
              'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
            )
            .min(1)
            .optional(),
          options: z
            .record(z.string(), z.any())
            .describe(
              'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
            )
            .optional()
            .nullable(),
        })
        .describe(
          '`catalogReference` item to send notifications for.\n\nCannot be used with `requestIds`.'
        )
        .optional(),
      requestIds: z.array(z.string()).optional(),
      extraAutomationTemplateParameters: z
        .record(z.string(), z.string())
        .describe(
          'Additional key-value pairs to pass to the back in stock notification template.'
        )
        .optional(),
    })
    .describe('Report options.')
    .optional(),
});
export const ReportItemsBackInStockResponse = z.object({});
